// Game-dev course demo: crates frozen in mid-air until something gives them a
// rule to obey.
//
// Drop them one at a time and it is you doing the work. Run `_physics_process`
// and every crate in the scene falls, bounces and settles on its own — which is
// the difference between animating a game and programming one.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Box, Play } from 'lucide-react';
import {
  Fitted,
  HaloRing,
  PLATFORM_TOP,
  ShadowBlob,
  useCloned,
  usePointerCursor,
} from '../helpers';
import DemoShell from './DemoShell';

/* ── Layout ──
   Spread across the deck so nothing has to collide with anything else: each
   crate owns its own column, falls straight down it, and lands on the floor.
   Crate-on-crate stacking would need a real solver to look right, and the
   point being made here does not depend on one. */
const CRATES = [
  { x: -1.5, z: 0.55, y: 1.5, size: 0.78, spin: 0.9 },
  { x: -0.75, z: -0.6, y: 2.5, size: 0.66, spin: -1.3 },
  { x: 0.0, z: 0.7, y: 1.9, size: 0.86, spin: 0.5 },
  { x: 0.8, z: -0.45, y: 3.0, size: 0.7, spin: -0.7 },
  { x: 1.55, z: 0.6, y: 2.1, size: 0.8, spin: 1.1 },
  { x: -0.1, z: -1.4, y: 2.7, size: 0.62, spin: -1.6 },
];
export const CRATE_COUNT = CRATES.length;

const GRAVITY = 11;
/** How much speed survives a bounce. Below RESTING the crate stops for good. */
const BOUNCE = 0.42;
const RESTING = 0.6;
/** Bob height and rate while a crate is still waiting for gravity. */
const FLOAT = 0.13;

const Crate = ({ index, spec, top, falling, accent, reduced, onLanded, onTap }) => {
  const group = useRef();
  const body = useRef({ y: 0, vy: 0, landed: false, roll: 0 });
  const [hovered, setHovered] = useState(false);
  usePointerCursor(hovered && !falling);
  const object = useCloned('/GodotBotBox.glb');

  const restY = top + spec.size / 2;

  // A crate that stops falling because the demo was reset has to forget it ever
  // landed, or it will never fall again.
  useEffect(() => {
    if (!falling) {
      body.current.y = 0;
      body.current.vy = 0;
      body.current.landed = false;
      body.current.roll = 0;
    }
  }, [falling]);

  useFrame(({ clock }, delta) => {
    const g = group.current;
    if (!g) return;
    const step = Math.min(delta, 0.05);
    const b = body.current;

    if (!falling) {
      // Waiting: hovering at its start height with a slow bob.
      const bob = reduced ? 0 : Math.sin(clock.getElapsedTime() * 1.1 + index) * FLOAT;
      g.position.y = THREE.MathUtils.damp(g.position.y, top + spec.y + bob, 6, step);
      g.rotation.set(0, spec.spin * 0.2, 0);
      g.scale.setScalar(hovered ? 1.07 : 1);
      return;
    }

    if (reduced) {
      // No fall to watch — put it where it would have ended up.
      g.position.y = restY;
      g.rotation.set(0, spec.spin * 0.2, 0);
      if (!b.landed) { b.landed = true; onLanded(index); }
      return;
    }

    if (b.y === 0) b.y = g.position.y;
    if (!b.landed) {
      b.vy -= GRAVITY * step;
      b.y += b.vy * step;
      b.roll += spec.spin * step * 0.8;

      if (b.y <= restY) {
        b.y = restY;
        if (Math.abs(b.vy) < RESTING) {
          b.vy = 0;
          b.landed = true;
          onLanded(index);
        } else {
          b.vy = -b.vy * BOUNCE;
        }
      }
    }

    g.position.y = b.y;
    // Tumbling while airborne, straightening out as it settles.
    const settle = b.landed ? 0 : 1;
    g.rotation.x = b.roll * 0.35 * settle;
    g.rotation.y = spec.spin * 0.2 + b.roll * settle;
    g.rotation.z = b.roll * 0.2 * settle;
    g.scale.setScalar(1);
  });

  return (
    <group
      ref={group}
      position={[spec.x, top + spec.y, spec.z]}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
      onClick={(e) => { e.stopPropagation(); if (!falling) onTap(index); }}
    >
      <Fitted object={object} size={spec.size} fit="max" align="center" />
      {/* A thin accent wire, so a crate still reads as "selected" while it is
          hovered on a dark plate. */}
      {hovered && !falling && (
        <mesh scale={spec.size * 1.04}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={accent} wireframe transparent opacity={0.6} toneMapped={false} />
        </mesh>
      )}
    </group>
  );
};

const Framing = ({ top }) => {
  const { camera, size } = useThree();

  useEffect(() => {
    const narrow = size.width < 640;
    camera.position.set(narrow ? 0.2 : 0.5, top + (narrow ? 3.6 : 3.3), narrow ? 8.4 : 7.8);
    camera.lookAt(0, top + 1.35, 0);
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height, top]);

  return null;
};

const Scene = ({ falling, accent, accentInk, reduced, onLanded, onTap }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const [top, setTop] = useState(PLATFORM_TOP);

  return (
    <>
      <Framing top={top} />

      <ambientLight intensity={0.5} />
      <hemisphereLight args={['#9DC3E6', '#0A1020', 0.6]} />
      <directionalLight position={[5, 9, 6]} intensity={1.25} />

      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.45} position={[0, top - 0.4, 0]} />

      {CRATES.map((spec, i) => (
        <Crate
          key={i}
          index={i}
          spec={spec}
          top={top}
          falling={falling.has(i)}
          accent={accent}
          reduced={reduced}
          onLanded={onLanded}
          onTap={onTap}
        />
      ))}

      <pointLight color={accent} intensity={1.8} distance={9} decay={2} position={[0, top + 2.4, 1.4]} />
    </>
  );
};

/* ── Demo ─────────────────────────────────────────────────────────────────── */

const CODE_LINES = [
  'func _physics_process(delta):',
  '    velocity.y -= GRAVITY * delta',
  '    move_and_slide()',
];

const PhysicsDemo = ({ accent, accentInk, reduced = false, onFirstUse, onRun }) => {
  const [falling, setFalling] = useState(() => new Set());
  const [landed, setLanded] = useState(() => new Set());
  const [running, setRunning] = useState(false);
  const announced = useRef(false);

  const allFalling = falling.size === CRATE_COUNT;
  const settled = landed.size === CRATE_COUNT;

  const drop = useCallback((index) => {
    setFalling((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
    if (!announced.current) {
      announced.current = true;
      if (onFirstUse) onFirstUse();
    }
  }, [onFirstUse]);

  const onLanded = useCallback((index) => {
    setLanded((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  /** The DOM equivalent of clicking a crate — the keyboard path in. */
  const dropOne = useCallback(() => {
    for (let i = 0; i < CRATE_COUNT; i += 1) {
      if (!falling.has(i)) { drop(i); return; }
    }
  }, [falling, drop]);

  const run = useCallback(() => {
    if (allFalling) return;
    setFalling(new Set(CRATES.map((_, i) => i)));
    setRunning(true);
    if (!announced.current) {
      announced.current = true;
      if (onFirstUse) onFirstUse();
    }
    if (onRun) onRun();
  }, [allFalling, onFirstUse, onRun]);

  useEffect(() => {
    if (running && settled) setRunning(false);
  }, [running, settled]);

  const reset = useCallback(() => {
    setRunning(false);
    setFalling(new Set());
    setLanded(new Set());
  }, []);

  return (
    <DemoShell
      camera={{ fov: 42, near: 0.1, far: 60, position: [0.5, 3.7, 7.8] }}
      scene={
        <Scene
          falling={falling}
          accent={accent}
          accentInk={accentInk}
          reduced={reduced}
          onLanded={onLanded}
          onTap={drop}
        />
      }
      fallback={
        <div className="demo-flat" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }} aria-hidden="true">
          {CRATES.map((_, i) => (
            <span key={i} className={falling.has(i) ? 'tile gone' : 'tile'} />
          ))}
        </div>
      }
      status={
        <>
          <Box size={16} aria-hidden="true" />
          נפלו <strong>{falling.size}</strong> מתוך {CRATE_COUNT} קופסאות
          {settled && <em> — הפיזיקה עשתה את השאר</em>}
        </>
      }
      code={{
        tag: 'GDSCRIPT',
        label: 'קוד GDScript לדוגמה: חוק הפיזיקה שגורם לאובייקטים ליפול',
        lines: CODE_LINES,
        activeLine: running ? 1 : null,
      }}
      controls={
        <>
          <button type="button" className="gg-btn-ghost" onClick={dropOne} disabled={allFalling}>
            <Box size={16} aria-hidden="true" /> הפילו קופסה ידנית
          </button>
          <button type="button" className="gg-btn" onClick={run} disabled={allFalling}>
            <Play size={16} aria-hidden="true" /> {running ? 'רץ...' : 'הריצו את המשחק'}
          </button>
        </>
      }
      hint={
        settled
          ? 'לא הזזנו אף קופסה. כתבנו חוק אחד — והמנוע החיל אותו על כל מה שבסצנה.'
          : 'אפשר גם ללחוץ ישירות על קופסה בסצנה. ואז להריץ את המשחק.'
      }
      onReset={reset}
      resetDisabled={falling.size === 0}
      resetLabel="החזרת הסצנה"
    />
  );
};

export default PhysicsDemo;
